import { useState, useEffect, useRef, useId } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { supabase } from '../supabaseClient';
import Avatar from './Avatar';
import { RING_HUES, hueSlot, noteHue, useHues, washColor } from '../lib/map/faceStyle';
import Button from './ui/Button';
import TextInput from './ui/TextInput';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPT_IMAGES = 'image/jpeg,image/png,image/webp';

const PHONE_REGEX = /^[+]?[\d\s\-()]{10,20}$/;

function isValidPhone(value) {
  if (!value || !value.trim()) return true;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15 && PHONE_REGEX.test(value.trim());
}

/** A social handle as stored: trimmed, no leading @ (the fields show the @). */
function normalizeHandle(raw) {
  if (raw == null || !String(raw).trim()) return null;
  let s = String(raw).trim();
  if (s.startsWith('@')) s = s.slice(1).trim();
  return s || null;
}

export function resizeImage(file, maxSize = 800) {
  return new Promise((resolve) => {
    const img = document.createElement('img');
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxSize && height <= maxSize) {
        resolve(file);
        return;
      }
      if (width > height) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);
          resolve(new File([blob], file.name, { type: file.type }));
        },
        file.type,
        0.9,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

/**
 * `submitRef` (the welcome flow's profile step): the form without its buttons; the caller saves through
 * submitRef.current(), which resolves true once saved and false when it could not.
 *
 * Without it (Edit Profile) the form saves as you go (Sam, 2026-10-30: the Save button was easy to miss): a photo the
 * moment it is picked or removed, text a moment after you stop typing, and whatever is left when you press Done.
 */
export default function EditProfileForm({ profile, userId, onDone, onSaved, submitRef = null }) {
  const autosave = !submitRef;
  const formId = useId();
  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [lastName, setLastName] = useState(profile?.last_name ?? '');
  const [zipCode, setZipCode] = useState(profile?.zip_code ?? '');
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  const [phoneNumber, setPhoneNumber] = useState(profile?.phone_number ?? '');
  const [instagramHandle, setInstagramHandle] = useState(
    (profile?.instagram_handle ?? '').replace(/^@+/, ''),
  );
  const [tiktokHandle, setTiktokHandle] = useState(
    (profile?.tiktok_handle ?? '').replace(/^@+/, ''),
  );
  // null = never picked: the name's hash decides (faceStyle hueSlot)
  const [avatarHue, setAvatarHue] = useState(profile?.avatar_hue ?? null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoToRemove, setPhotoToRemove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // The photo as last written, so a later text save keeps a photo saved since the form opened
  const photoUrlRef = useRef(profile?.profile_photo_url ?? null);
  const [error, setError] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const fileInputRef = useRef(null);

  const isFieldEmpty = (value) => !value || !String(value).trim();

  useEffect(() => {
    setFirstName(profile?.first_name ?? '');
    setLastName(profile?.last_name ?? '');
    setZipCode(profile?.zip_code ?? '');
    setDisplayName(profile?.display_name ?? '');
    setPhoneNumber(profile?.phone_number ?? '');
    setInstagramHandle((profile?.instagram_handle ?? '').replace(/^@+/, ''));
    setTiktokHandle((profile?.tiktok_handle ?? '').replace(/^@+/, ''));
    setAvatarHue(profile?.avatar_hue ?? null);
    setPhotoPreview(null);
    setPhotoToRemove(false);
    photoUrlRef.current = profile?.profile_photo_url ?? null;
    setError('');
    setDisplayNameError('');
    setPhoneError('');
    // On the saved values, not the object: the phone paints the stored profile and then swaps in the fetched copy (and
    // auth refreshes swap it again), and each swap wiped what you had typed - a first and last name entered on the
    // welcome step never reached the save (Sam, 2026-10-26).
  }, [
    profile?.id,
    profile?.first_name,
    profile?.last_name,
    profile?.zip_code,
    profile?.display_name,
    profile?.phone_number,
    profile?.instagram_handle,
    profile?.tiktok_handle,
    profile?.avatar_hue,
    profile?.profile_photo_url,
  ]);

  // quiet: the autosave checks without saying anything mid-word; the errors show when you leave the field or press Done
  function validate(quiet = false) {
    let valid = true;
    if (!displayName.trim()) {
      if (!quiet) setDisplayNameError('Display name is required');
      valid = false;
    } else if (!quiet) {
      setDisplayNameError('');
    }
    if (!isValidPhone(phoneNumber)) {
      if (!quiet) setPhoneError('Please enter a valid phone number (10–15 digits)');
      valid = false;
    } else if (!quiet) {
      setPhoneError('');
    }
    return valid;
  }

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      setError('Please choose a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be 5MB or smaller.');
      return;
    }
    setError('');
    setPhotoToRemove(false);
    const url = URL.createObjectURL(file);
    setPhotoPreview({ file, url });
  }

  function handleRemovePhoto() {
    setPhotoPreview(null);
    setPhotoToRemove(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError('');
  }

  async function handleSubmit(e, { quiet = false } = {}) {
    e?.preventDefault?.();
    setError('');
    if (!validate(quiet)) return false;
    if (!userId) return false;
    setSaving(true);
    setSaved(false);
    try {
      let profilePhotoUrl = photoUrlRef.current;

      if (photoToRemove && profilePhotoUrl) {
        try {
          const { data: list } = await supabase.storage.from('profile-photos').list(userId);
          if (list?.length) {
            const paths = list.map((o) => `${userId}/${o.name}`);
            await supabase.storage.from('profile-photos').remove(paths);
          }
        } catch {
          /* ignore storage cleanup errors */
        }
        profilePhotoUrl = null;
      } else if (photoPreview?.file) {
        const file = await resizeImage(photoPreview.file);
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const filePath = `${userId}/avatar.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(filePath, file, { upsert: true });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from('profile-photos').getPublicUrl(filePath);
        const baseUrl = data.publicUrl.split('?')[0];
        profilePhotoUrl = `${baseUrl}?t=${Date.now()}`;
        // Fetched now, so the Profile page and the header have it in the browser's cache instead of loading it after
        new Image().src = profilePhotoUrl;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          first_name: firstName?.trim() || null,
          last_name: lastName?.trim() || null,
          display_name: displayName.trim(),
          instagram_handle: normalizeHandle(instagramHandle),
          tiktok_handle: normalizeHandle(tiktokHandle),
          profile_photo_url: profilePhotoUrl,
          avatar_hue: avatarHue,
        })
        .eq('id', userId);
      if (updateError) throw updateError;
      noteHue(displayName, avatarHue); // a renamed profile keeps its colour under the new name
      // Phone and zip are owner-only (165): they live in profile_private, not on the public profiles row.
      const { error: privateError } = await supabase.rpc('update_my_private_profile', {
        p_phone_number: phoneNumber.trim() || null,
        p_zip_code: zipCode?.trim() || null,
      });
      if (privateError) throw privateError;
      photoUrlRef.current = profilePhotoUrl;
      // The upload is done: the preview stays on screen but is not uploaded again by the next save
      if (photoPreview?.file) setPhotoPreview({ url: photoPreview.url });
      await onSaved?.();
      setSaved(true);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      return false;
    } finally {
      setSaving(false);
    }
  }

  if (submitRef) submitRef.current = handleSubmit;

  // Autosave: what is on screen against what was last written. A picked or removed photo saves at once, typing after
  // a pause. One save at a time; a change made during one is picked up when it finishes. A state that failed to save
  // is not retried on its own (the error shows; the next edit or Done tries again).
  const photoPending = Boolean(photoPreview?.file) || Boolean(photoToRemove && photoUrlRef.current);
  const key = JSON.stringify([
    firstName,
    lastName,
    zipCode,
    displayName,
    phoneNumber,
    instagramHandle,
    tiktokHandle,
    avatarHue,
    photoPreview?.url ?? null,
    photoToRemove,
  ]);
  const savedKeyRef = useRef(key);
  const failedKeyRef = useRef(null);
  const inflightRef = useRef(null);
  const saveRef = useRef(null);
  saveRef.current = async (loud) => {
    const k = key;
    const ok = await handleSubmit(null, { quiet: !loud });
    if (ok) savedKeyRef.current = k;
    else failedKeyRef.current = k;
    return ok;
  };
  useEffect(() => {
    savedKeyRef.current = key; // a fresh profile is the saved state
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the profile itself changes
  const dirty = key !== savedKeyRef.current;
  useEffect(() => {
    if (!autosave || !dirty || saving || key === failedKeyRef.current) return undefined;
    const t = setTimeout(
      () => {
        inflightRef.current = saveRef.current(false).finally(() => (inflightRef.current = null));
      },
      photoPending ? 0 : 900,
    );
    return () => clearTimeout(t);
  }); // every render: the timer restarts while you type
  async function handleDone() {
    await inflightRef.current; // a save already running finishes first
    if (key !== savedKeyRef.current && !(await saveRef.current(true))) return;
    onDone?.();
  }

  // Your colour shows at once everywhere (the store), then saves with the form; an unpicked profile shows its hash
  useHues();
  const shownHue = avatarHue ?? hueSlot(displayName || profile?.display_name);
  function pickHue(i) {
    setAvatarHue(i);
    noteHue(displayName || profile?.display_name, i);
  }

  return (
    <div className="flex flex-col gap-6">
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="rounded-lg bg-danger-fill p-3 text-sm text-danger" role="alert">
            {error}
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            className="group relative !h-auto !w-auto !min-h-0 !rounded-full !border-0 !bg-transparent !p-0 cursor-pointer whitespace-normal shadow-none hover:!bg-transparent hover:!shadow-none focus-visible:!ring-offset-2 focus-visible:!ring-primary dark:focus-visible:!ring-offset-dark-card"
          >
            {photoPreview?.url ? (
              <img
                src={photoPreview.url}
                alt="Preview"
                className="h-24 w-24 rounded-full border-2 border-gray-200 object-cover dark:border-dark-border"
              />
            ) : photoToRemove ? (
              <Avatar
                displayName={displayName || profile?.email}
                profilePhotoUrl={null}
                size="lg"
              />
            ) : (
              <div
                className={`rounded-full ${
                  !profile?.profile_photo_url
                    ? 'ring-2 ring-danger ring-offset-2 dark:ring-offset-dark-card'
                    : ''
                }`}
              >
                <Avatar
                  displayName={displayName || profile?.email}
                  profilePhotoUrl={profile?.profile_photo_url}
                  size="lg"
                />
              </div>
            )}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/0 transition-colors group-hover:bg-black/30">
              <Camera className="h-6 w-6 text-ink opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </Button>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_IMAGES}
              onChange={handlePhotoChange}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              leftIcon={<Camera className="h-4 w-4" />}
            >
              Change Photo
            </Button>
            {(profile?.profile_photo_url || photoPreview?.url) && !photoToRemove && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleRemovePhoto}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                Remove
              </Button>
            )}
            {photoToRemove && !autosave && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPhotoToRemove(false)}
                className="!h-auto py-1.5 text-sm font-medium !text-primary hover:underline underline-offset-2"
              >
                Undo remove
              </Button>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-dark-muted">JPG, PNG or WebP. Max 5MB.</p>
          {!profile?.profile_photo_url && !photoPreview?.url && !photoToRemove && (
            <p className="text-xs font-medium text-accent-soft">
              Add a profile photo to complete your profile
            </p>
          )}
        </div>

        <div>
          <span
            id="edit-color-label"
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            Color
          </span>
          <div
            role="radiogroup"
            aria-labelledby="edit-color-label"
            className="grid grid-cols-10 gap-1.5"
          >
            {RING_HUES.map((c, i) => {
              const on = i === shownHue;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={`Color ${i + 1}`}
                  onClick={() => pickHue(i)}
                  // one line on a phone: the ten share the width, up to 36 px each (Sam's pick, 2026-11-01)
                  className="aspect-square w-full max-w-9 rounded-full transition-[box-shadow,transform] duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  style={{
                    // each swatch is a face in that color: the wash inside the ring
                    backgroundColor: washColor(c),
                    // the picked one: the Own glow, as on your profile photo and the corner avatar
                    boxShadow: on
                      ? `inset 0 0 0 3px ${c}, 0 0 12px ${c}b3`
                      : `inset 0 0 0 2px ${c}`,
                    transform: on ? 'scale(1.1)' : undefined,
                  }}
                />
              );
            })}
          </div>
        </div>

        <div className="flex gap-3">
          <div className="min-w-0 flex-1">
            <label
              htmlFor="edit-first-name"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
            >
              First name {isFieldEmpty(firstName) && <span className="text-accent-soft">*</span>}
            </label>
            <TextInput
              id="edit-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              invalid={isFieldEmpty(firstName)}
              placeholder="First name"
              autoComplete="given-name"
            />
          </div>
          <div className="min-w-0 flex-1">
            <label
              htmlFor="edit-last-name"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
            >
              Last name
            </label>
            <TextInput
              id="edit-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              autoComplete="family-name"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="edit-display-name"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            Display name <span className="text-accent-soft">*</span>
          </label>
          <TextInput
            id="edit-display-name"
            type="text"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setDisplayNameError('');
            }}
            invalid={Boolean(displayNameError)}
            onBlur={() => validate()}
            placeholder="Your display name"
            autoComplete="name"
          />
          {displayNameError && <p className="mt-1 text-sm text-danger">{displayNameError}</p>}
        </div>

        <div>
          <label
            htmlFor="edit-zip"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            Zip code
          </label>
          <TextInput
            id="edit-zip"
            type="text"
            inputMode="numeric"
            maxLength={10}
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="Zip code"
            autoComplete="postal-code"
          />
        </div>

        <div>
          <label
            htmlFor="edit-phone"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            Phone number
          </label>
          <TextInput
            id="edit-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setPhoneError('');
            }}
            invalid={Boolean(phoneError)}
            onBlur={() => validate()}
            placeholder="(555) 123-4567"
          />
          {phoneError && <p className="mt-1 text-sm text-danger">{phoneError}</p>}
        </div>

        <div>
          <label
            htmlFor="edit-instagram"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            Instagram
          </label>
          {/* The @ is part of the field, so a handle goes in bare without a note saying so (Sam, 2026-10-31). */}
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-dark-muted"
            >
              @
            </span>
            <TextInput
              id="edit-instagram"
              type="text"
              className="!pl-7"
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value.replace(/^@+/, ''))}
              placeholder="username"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>
        <div>
          <label
            htmlFor="edit-tiktok"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text"
          >
            TikTok
          </label>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-dark-muted"
            >
              @
            </span>
            <TextInput
              id="edit-tiktok"
              type="text"
              className="!pl-7"
              value={tiktokHandle}
              onChange={(e) =>
                setTiktokHandle(e.target.value.replace(/[^A-Za-z0-9_.]/g, '').slice(0, 24))
              }
              placeholder="username"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>
      </form>

      {autosave && (
        <div className="flex flex-col items-center gap-2">
          <p className="h-4 text-xs text-dark-muted" aria-live="polite">
            {saving ? 'Saving…' : saved && !dirty ? 'Saved' : ''}
          </p>
          <Button fullWidth onClick={handleDone}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
}
